import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute } from "@angular/router";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import LocalizationConstants from "src/app/constants/localization-constants";
import RouteNameConstants from "src/app/constants/route-name-constants";
import { Account } from "src/app/models/Account";
import { ModalService } from "src/app/services/modal.service";
import { PendingUserService } from "src/app/services/pending-user.service";

import { ConfirmationDialogData } from "../../common/confirmation-dialog/confirmation-dialog-modal.component";

@Component({
  selector: "app-pending-users",
  templateUrl: "./pending-users.component.html",
  styleUrls: ["./pending-users.component.scss"],
  standalone: false,
})
export class PendingUsersComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  readonly routeNameConstants = RouteNameConstants;
  readonly localizationConstants = LocalizationConstants;

  private subscriptions: Subscription[] = [];

  faSpinner = faSpinner;
  dataSource = new MatTableDataSource<Account>([]);
  isLoading: boolean;

  pageSize: number = 10;

  columns = [
    { key: "country", title: LocalizationConstants.PENDING_USERS_COUNTRY },
    { key: "firstName", title: LocalizationConstants.PENDING_USERS_NAME },
    { key: "email", title: LocalizationConstants.USER_EMAIL },
    { key: "company", title: LocalizationConstants.PENDING_USERS_COMPANY },
    {
      key: "requestDate",
      title: LocalizationConstants.PENDING_USERS_REQUEST_DATE,
    },
    { key: "address", title: LocalizationConstants.PENDING_USERS_ADDRESS },
  ];

  columnKeysAll = [...this.columns.map((c) => c.key), "action1", "action2"];

  constructor(
    private modalService: ModalService,
    public pendingUsers: PendingUserService,
    public translate: TranslateService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit() {
    // get the searchParam from queryString (used for filtering for pending user in email remainder)
    this.dataSource.filter = this.route.snapshot.queryParams["search"] ?? "";
    this.getList();
  }

  ngAfterViewInit() {
    this.dataSource.filterPredicate = this.quickSearchPredicate;
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  getList() {
    this.isLoading = true;
    this.pendingUsers.getList().subscribe(
      (users) => {
        if (!users) return;

        this.isLoading = false;
        this.dataSource.data = users;
      },
      () => (this.isLoading = false),
    );
  }

  onRemoveUser(user: Account) {
    const resultObservable = this.modalService.openConfirmationModal(
      new ConfirmationDialogData(LocalizationConstants.PENDING_USERS_CONFIRM_REMOVE_DIALOG_TITLE, [
        LocalizationConstants.PENDING_USERS_CONFIRM_REMOVE_FROM_LIST,
      ]),
    );

    const subscription = resultObservable.subscribe((result) => {
      if (result) {
        this.removeUser(user);
      }
    });

    this.subscriptions.push(subscription);
  }

  private removeUser(user: Account) {
    user.pending = true;
    this.pendingUsers.remove(user.id).subscribe(() => {
      user.pending = false;
      this.dataSource.data = this.dataSource.data.filter((u) => u.id != user.id);
    });
  }

  private quickSearchPredicate(user: Account, searchValue: string): boolean {
    if (!searchValue) return true;
    searchValue = searchValue.toLowerCase();
    return (
      (user.firstName + " " + user.lastName).toLowerCase().includes(searchValue) ||
      user.lastName?.toLowerCase().includes(searchValue) ||
      user.country?.toLowerCase().includes(searchValue) ||
      user.company?.toLowerCase().includes(searchValue) ||
      user.email?.toLowerCase().includes(searchValue) ||
      user.address?.toLowerCase().includes(searchValue) ||
      user.referenceId === searchValue
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }
}
